import { Injectable } from '@angular/core';
import { distinctUntilArrayItemChanged, QueryEntity } from '@datorama/akita';
import { CompaniesState, CompaniesStore } from './companies.store';
import {
    checkFilter,
    flattenNestedObject,
    InferFilter
} from 'src/app/shared/state/query-filter.functions';
import { get } from 'dot-prop';
import { Company } from './company.model';
import { PositionsQuery } from '../../../../features/positions/state/positions.query';
import { combineLatest, of, switchMap } from 'rxjs';
import { Position } from '../../../../features/positions/state/position.model';
import { Instrument } from '../../../../shared/state/instruments/instrument.model';
import { filter, map, shareReplay } from 'rxjs/operators';
import { AccountTypeEnum } from '../../../../shared/state/accts/acct.model';
import { CompaniesService } from './companies.service';
import { AcctsQuery } from '../../../../shared/state/accts/accts.query';
import { InstrumentsQuery } from '../../../../shared/state/instruments/instruments.query';

@Injectable({ providedIn: 'root' })
export class CompaniesQuery extends QueryEntity<CompaniesState> {
    constructor(
        protected store: CompaniesStore,
        private _companiesService: CompaniesService,
        private _positionsQuery: PositionsQuery,
        private _accountsQuery: AcctsQuery,
        private instrumentsQuery: InstrumentsQuery
    ) {
        super(store);
        this._companiesService.get().subscribe();
    }

    allAsObject$ = this.selectAll({ asObject: true });

    isLoading$ = this.selectLoading();

    selectByISIN$(isin: string) {
        return this.selectEntity(isin);
    }

    selectAllEquities$ = () => {
        return this.instrumentsQuery
            .selectInstrumentsByFilter$<Instrument>({
                type: { eq: 'EQUITY' },
                deleted: { eq: false }
            })
            .pipe(shareReplay(1));
    };

    getAccumulatedEquityPositions$ = (showSoldOff: boolean) => {
        return combineLatest([
            this.selectAllEquities$(),
            this._accountsQuery.selectActiveAccounts$
        ]).pipe(
            switchMap(([equities, accounts]) => {
                const equityIds = equities.map((e) => e.instrumentId);
                return this._positionsQuery.selectPositionsByFilter$({
                    instrument: { instrumentId: { in: equityIds } },
                    account: {
                        accountType: { nin: [AccountTypeEnum.BROKER] },
                        accountId: { in: accounts.map((a) => a.accountId) }
                    }
                });
            }),
            map((positions: Position[]): Position[] => {
                const grouped: { [key: string]: Position } = {};

                for (const pos of positions) {
                    const isin = pos.instrument.isin;
                    const sign = pos.position.quantity >= 0 ? 'positive' : 'negative';
                    const key = `${isin}_${sign}`;

                    if (!grouped[key]) {
                        grouped[key] = {
                            ...pos,
                            position: { ...pos.position }
                        };
                    } else {
                        grouped[key].position.quantity += pos.position.quantity;
                        grouped[key].marketValue += pos.marketValue;
                        grouped[key].acquisitionValue += pos.acquisitionValue;
                        grouped[key].unrealizedPnLAvg += pos.unrealizedPnLAvg;
                        grouped[key].position.realizedPnLAvg +=
                            pos.position.realizedPnLAvg;
                        grouped[key].position.totalDividend += pos.position.totalDividend;
                    }
                }

                for (const key in grouped) {
                    grouped[key].position.acquisitionPriceAvg =
                        grouped[key].acquisitionValue / grouped[key].position.quantity;
                }

                const groupedValues = Object.values(grouped);
                return showSoldOff
                    ? groupedValues.filter(
                          (equity) =>
                              !(
                                  equity.position.realizedQuantity === 0 &&
                                  equity.position.quantity === 0
                              )
                      )
                    : groupedValues.filter((equity) => equity.position.quantity !== 0);
            }),
            shareReplay(1)
        );
    };

    selectCompaniesByFilter$(filters: InferFilter<Company> = {}) {
        return this.selectAll({
            filterBy: (company) => {
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(company, key);
                        return !checkFilter(value, criteria);
                    });
                } else {
                    return true;
                }
            }
        });
    }

    selectPositionsForCompanyIds$ = (
        instrumentIds: Array<Instrument['instrumentId']>,
        positionFilter: InferFilter<Position> = {
            instrument: {
                instrumentId: {
                    in: instrumentIds
                },
                type: {
                    in: ['EQUITY', 'BOND', 'FUND', 'ETF']
                }
            },
            position: {
                quantity: {
                    ne: 0
                }
            },
            marketValueInBaseCurrency: {
                fn: (value, object) => {
                    // filter out positions that are considered netted
                    return value > 0.001 || value < -0.001;
                }
            }
        }
    ) => {
        return combineLatest([
            this._accountsQuery.selectActiveAccounts$.pipe(
                map((accounts) => accounts.map((account) => account.accountId)),
                distinctUntilArrayItemChanged(),
                switchMap((accountsIds) => {
                    return this._positionsQuery.selectPositionsByFilter$({
                        ...positionFilter,
                        account: {
                            accountId: {
                                in: accountsIds
                            }
                        }
                    });
                })
            ),
            this._positionsQuery.isLoading$
        ]).pipe(
            filter(([_, loading]) => !loading),
            map(([positions, _]) => {
                const companies = {};
                for (let i = 0; i < positions.length; i++) {
                    const position = positions[i];
                    const companyId = position.instrument.instrumentId;
                    if (!companies[companyId]) {
                        companies[companyId] = {
                            all: []
                        };
                    }
                    companies[companyId].all.push(position);
                }
                return companies;
            })
        );
    };

    equityPositions$ = () => {
        return combineLatest([
            this.selectAllEquities$(),
            this._accountsQuery.selectActiveAccounts$
        ]).pipe(
            switchMap(([equities, accounts]) => {
                const equityIds = equities.map((f) => f.instrumentId);
                if (equityIds.length === 0) {
                    return of([]);
                }
                return this._positionsQuery.selectPositionsByFilter$({
                    instrument: { instrumentId: { in: equityIds } },
                    account: {
                        accountType: { nin: [AccountTypeEnum.BROKER] },
                        accountId: { in: accounts.map((a) => a.accountId) }
                    },
                    position: {
                        quantity: { ne: 0 }
                    }
                });
            }),
            shareReplay(1)
        );
    };
}
